import { useEffect, useRef, useState } from 'react';
import { cameraEncoding, isCamQualityWire, type CamQualityWire } from '../utils/mediaBudget';
import { openPeerConnections } from '../utils/rtcRegistry';

const APPLY_MS = 2000;
const ASK_MS = 5000;

type Enc = ReturnType<typeof cameraEncoding>;
const KEYS = ['maxBitrate', 'maxFramerate', 'scaleResolutionDownBy'] as const;

/** One sender's first encoding brought to what the budget says; the encoder's own choice where it says nothing. */
async function applyTo(sender: RTCRtpSender, want: Enc): Promise<void> {
  const params = sender.getParameters();
  if (!params.encodings?.length) return;
  const e = params.encodings[0] as RTCRtpEncodingParameters & Record<string, unknown>;
  let changed = false;
  for (const k of KEYS) {
    const v = want[k];
    if (v === undefined) { if (e[k] !== undefined) { delete e[k]; changed = true; } } else if (e[k] !== v) { e[k] = v; changed = true; }
  }
  if (changed) await sender.setParameters(params).catch(() => { /* refused by this browser: the encoder keeps its own */ });
}

/**
 * The phone budget in the call (utils/mediaBudget): this side's camera sender held to the budget, and the
 * other side asked over the room's channel to send its camera low while this phone has the match or a
 * share on its stage; their ask applied here in turn. Re-applied every 2 s, since senders come and go
 * with each connection.
 */
export function useMediaBudget(p: {
  on: boolean; phone: boolean; localStream: MediaStream | null; wantFarLow: boolean;
  sendData: (d: unknown) => void;
  onData: (cb: (from: string, d: unknown) => void) => () => void;
}): { farWantsLow: boolean } {
  const { on, phone, localStream, wantFarLow, sendData, onData } = p;
  const [farWantsLow, setFarWantsLow] = useState(false);
  useEffect(() => onData((_from, d) => { if (isCamQualityWire(d)) setFarWantsLow(d.low); }), [onData]);

  // The ask: as it changes, and again every few seconds, so a side that joins late hears it.
  const sendRef = useRef(sendData);
  useEffect(() => { sendRef.current = sendData; });
  useEffect(() => {
    const ask = () => sendRef.current({ t: 'camq', low: on && wantFarLow } satisfies CamQualityWire);
    ask();
    const id = setInterval(ask, ASK_MS);
    return () => clearInterval(id);
  }, [on, wantFarLow]);

  useEffect(() => {
    const track = localStream?.getVideoTracks()[0];
    if (!track) return;
    const apply = () => {
      const st = track.getSettings();
      const want = on ? cameraEncoding({ phone, farWantsLow, trackW: st.width ?? null, trackH: st.height ?? null }) : {};
      for (const pc of openPeerConnections()) {
        for (const s of pc.getSenders()) if (s.track === track) void applyTo(s, want);
      }
    };
    apply();
    const id = setInterval(apply, APPLY_MS);
    return () => clearInterval(id);
  }, [on, phone, farWantsLow, localStream]);

  return { farWantsLow };
}
